import { useState, useRef, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Volume2, ChevronDown, AlertCircle, RotateCcw } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import MessageBubbleRedesigned from "@/components/MessageBubbleRedesigned";
import NexiComposer from "@/components/NexiComposer";
import NexiLivingHome from "@/components/NexiLivingHome";
import NexiOrb from "@/components/NexiOrb";
import { usePremium } from "@/context/PremiumContext";
import { incrementMessageCount } from "@/components/ReviewPrompt";
import { getDeviceId } from "@/utils/deviceId";
import { useNexiVoice } from "@/hooks/useNexiVoice";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * ChatInterfaceRedesigned - Premium AI conversation interface
 * Fixed: Mobile layout, scrolling, voice input, error handling
 */
const ChatInterfaceRedesigned = ({ mode, conversationId, onConversationChange, userName }) => {
  // Message state
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isSending, setIsSending] = useState(false);
  
  // Attachment state
  const [selectedImage, setSelectedImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [fileStatus, setFileStatus] = useState(null);
  
  // UI state
  const [orbState, setOrbState] = useState("idle");
  const [showScrollButton, setShowScrollButton] = useState(false);
  const [failedMessage, setFailedMessage] = useState(null);
  
  // Audio state
  const [audioEnabled, setAudioEnabled] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(false);
  
  // Refs
  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const synthRef = useRef(window.speechSynthesis);
  const isStreamingRef = useRef(false);
  const userScrolledRef = useRef(false);
  const lastMessageCountRef = useRef(0);
  
  // Voice recognition
  const {
    voiceState,
    transcript,
    interimTranscript,
    fullTranscript,
    startListening,
    stopListening,
    clearTranscript,
    error: voiceError,
    isSupported: voiceSupported
  } = useNexiVoice();
  
  const { 
    canAskQuestion, 
    useQuestion, 
    promptUpgrade
  } = usePremium();

  // Update input when voice transcript changes
  useEffect(() => {
    if (fullTranscript) {
      setInput(fullTranscript);
    }
  }, [fullTranscript]);

  // Update orb state based on voice
  useEffect(() => {
    if (voiceState === "listening") {
      setOrbState("listening");
    } else if (voiceState === "processing") {
      setOrbState("thinking");
    } else if (voiceState === "error") {
      setOrbState("error");
      setTimeout(() => setOrbState("idle"), 2000);
    } else if (!isLoading) {
      setOrbState("idle");
    }
  }, [voiceState, isLoading]);

  // Show voice errors
  useEffect(() => {
    if (voiceError) {
      toast.error(voiceError);
    }
  }, [voiceError]);

  // Smart scroll
  const scrollToBottom = useCallback((force = false) => {
    if (!messagesContainerRef.current || !messagesEndRef.current) return;
    
    const container = messagesContainerRef.current;
    const { scrollTop, scrollHeight, clientHeight } = container;
    const distanceFromBottom = scrollHeight - scrollTop - clientHeight;
    const isNearBottom = distanceFromBottom < 150;
    
    if (force || isNearBottom || !userScrolledRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: "smooth", block: "end" });
      userScrolledRef.current = false;
      setShowScrollButton(false);
    }
  }, []);

  // Handle scroll events
  const handleScroll = useCallback(() => {
    if (!messagesContainerRef.current) return;
    
    const container = messagesContainerRef.current;
    const { scrollTop, scrollHeight, clientHeight } = container;
    const distanceFromBottom = scrollHeight - scrollTop - clientHeight;
    
    if (distanceFromBottom > 200) {
      userScrolledRef.current = true;
      setShowScrollButton(true);
    } else {
      userScrolledRef.current = false;
      setShowScrollButton(false);
    }
  }, []);

  // Auto-scroll when new messages arrive
  useEffect(() => {
    if (messages.length > lastMessageCountRef.current) {
      const lastMessage = messages[messages.length - 1];
      if (lastMessage?.role === "user") {
        scrollToBottom(true);
      } else {
        scrollToBottom(false);
      }
    }
    lastMessageCountRef.current = messages.length;
  }, [messages.length, scrollToBottom]);

  // Scroll during streaming
  useEffect(() => {
    const streamingMsg = messages.find(m => m.streaming);
    if (streamingMsg && !userScrolledRef.current) {
      scrollToBottom(false);
    }
  }, [messages, scrollToBottom]);

  // Load messages when conversation changes
  useEffect(() => {
    if (!conversationId) {
      setMessages([]);
      return;
    }
    if (!isStreamingRef.current) {
      loadMessages();
    }
  }, [conversationId]);

  // Cleanup
  useEffect(() => {
    return () => {
      if (synthRef.current) {
        synthRef.current.cancel();
      }
    };
  }, []);

  const loadMessages = async () => {
    try {
      const response = await axios.get(`${API}/conversations/${conversationId}/messages`);
      setMessages(response.data);
      setTimeout(() => scrollToBottom(true), 100);
    } catch (error) {
      console.error("Error loading messages:", error);
    }
  };

  const speakText = (text) => {
    if (!audioEnabled || !text) return;
    synthRef.current.cancel();
    const utterance = new SpeechSynthesisUtterance(text);
    utterance.rate = 1.0;
    utterance.pitch = 1.0;
    utterance.volume = 1.0;
    utterance.onstart = () => setIsSpeaking(true);
    utterance.onend = () => setIsSpeaking(false);
    utterance.onerror = () => setIsSpeaking(false);
    synthRef.current.speak(utterance);
  };

  // File handling
  const handleFileSelect = (file) => {
    if (!file) return;
    const allowedTypes = ['.pdf', '.txt', '.doc', '.docx', '.csv', '.json', '.md'];
    const ext = '.' + file.name.split('.').pop().toLowerCase();
    const maxSize = 10 * 1024 * 1024;
    
    if (file.size > maxSize) {
      toast.error("File too large. Maximum size is 10MB.");
      return;
    }
    
    if (allowedTypes.includes(ext) || file.type.includes('pdf') || file.type.includes('text')) {
      setSelectedFile(file);
      setFileStatus('ready');
      setSelectedImage(null);
      setImagePreview(null);
      toast.success(`File ready: ${file.name}`);
    } else {
      toast.error("Supported files: PDF, TXT, DOC, DOCX, CSV, JSON, MD");
    }
  };

  const handleImageSelect = (file) => {
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.error("Please select an image file");
      return;
    }
    setSelectedImage(file);
    setSelectedFile(null);
    setFileStatus(null);
    const reader = new FileReader();
    reader.onloadend = () => setImagePreview(reader.result);
    reader.readAsDataURL(file);
  };

  const handleRemoveFile = () => {
    setSelectedFile(null);
    setFileStatus(null);
  };

  const handleRemoveImage = () => {
    setSelectedImage(null);
    setImagePreview(null);
  };

  // Voice orb click handler - ACTUAL IMPLEMENTATION
  const handleVoiceClick = useCallback(() => {
    if (!voiceSupported) {
      toast.error("Voice input is not supported in this browser. Please use Chrome or Safari.");
      return;
    }
    
    if (voiceState === "listening") {
      stopListening();
    } else {
      clearTranscript();
      startListening();
    }
  }, [voiceState, voiceSupported, startListening, stopListening, clearTranscript]);

  // Retry failed message
  const handleRetry = useCallback(() => {
    if (failedMessage) {
      setInput(failedMessage.content);
      setFailedMessage(null);
    }
  }, [failedMessage]);

  // Main send function
  const handleSend = async () => {
    if (isSending || isLoading) return;
    if (!input.trim() && !selectedImage && !selectedFile) return;

    if (!canAskQuestion()) {
      promptUpgrade();
      return;
    }

    setIsSending(true);
    clearTranscript();

    const messageId = `msg-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    const userMessage = {
      id: messageId,
      role: "user",
      content: input || (selectedImage ? "What's in this image?" : `Analyze this file: ${selectedFile?.name}`),
      createdAt: new Date().toISOString(),
      timestamp: new Date().toISOString(),
      image: imagePreview,
      fileName: selectedFile?.name,
      hasDocument: !!selectedFile,
      status: "sending"
    };

    const currentInput = input;
    const currentImage = selectedImage;
    const currentFile = selectedFile;
    const currentImagePreview = imagePreview;

    setMessages((prev) => [...prev, userMessage]);
    setInput("");
    setSelectedImage(null);
    setImagePreview(null);
    setSelectedFile(null);
    setFileStatus(null);
    setFailedMessage(null);
    
    useQuestion();
    setIsLoading(true);
    isStreamingRef.current = true;
    setOrbState(currentFile ? "file_analysis" : "thinking");

    const streamingMessageId = `assistant-${Date.now()}`;
    setMessages((prev) => [
      ...prev,
      {
        id: streamingMessageId,
        role: "assistant",
        content: currentFile ? "📄 Processing document..." : "",
        createdAt: new Date().toISOString(),
        timestamp: new Date().toISOString(),
        streaming: true,
        status: "streaming"
      }
    ]);

    try {
      let response;
      const deviceId = getDeviceId();

      if (currentImage) {
        const formData = new FormData();
        formData.append("message", userMessage.content);
        formData.append("mode", mode);
        formData.append("user_name", userName);
        formData.append("user_id", deviceId);
        if (conversationId) formData.append("conversation_id", conversationId);
        formData.append("image", currentImage);

        response = await fetch(`${API}/chat/image`, { method: "POST", body: formData });
      } else if (currentFile) {
        const formData = new FormData();
        formData.append("message", userMessage.content);
        formData.append("mode", mode);
        formData.append("user_name", userName);
        formData.append("user_id", deviceId);
        if (conversationId) formData.append("conversation_id", conversationId);
        formData.append("document", currentFile);

        response = await fetch(`${API}/chat/document`, { method: "POST", body: formData });
      } else {
        setOrbState("generating");
        response = await fetch(`${API}/chat/stream`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            message: userMessage.content,
            mode: mode,
            conversation_id: conversationId,
            user_name: userName,
            user_id: deviceId
          })
        });
      }

      setMessages((prev) =>
        prev.map((msg) => msg.id === messageId ? { ...msg, status: "sent" } : msg)
      );

      if (currentImage || currentFile) {
        if (!response.ok) throw new Error(`Upload failed: ${response.status}`);
        
        const data = await response.json();
        
        if (data.extraction_failed) {
          setMessages((prev) =>
            prev.map((msg) =>
              msg.id === streamingMessageId
                ? { ...msg, content: data.response, streaming: false, status: "complete", extractionFailed: true }
                : msg
            )
          );
          toast.error("Could not read document contents");
        } else if (!data.response) {
          throw new Error("No response from AI");
        } else {
          setMessages((prev) =>
            prev.map((msg) =>
              msg.id === streamingMessageId
                ? { ...msg, content: data.response, streaming: false, status: "complete" }
                : msg
            )
          );
          if (currentFile) toast.success("Document analyzed!");
          speakText(data.response);
        }
        
        if (!conversationId && data.conversation_id) {
          onConversationChange(data.conversation_id);
        }
        
        setOrbState("complete");
        setTimeout(() => setOrbState("idle"), 2000);
      } else {
        // Handle streaming
        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let fullContent = "";
        let newConversationId = conversationId;
        let receivedImages = [];

        while (true) {
          const { value, done } = await reader.read();
          if (done) break;

          const chunk = decoder.decode(value);
          const lines = chunk.split("\n");

          for (const line of lines) {
            if (line.startsWith("data: ")) {
              try {
                const data = JSON.parse(line.slice(6));
                
                if (data.conversation_id && !newConversationId) {
                  newConversationId = data.conversation_id;
                  onConversationChange(data.conversation_id);
                }

                if (data.type === "content") {
                  fullContent += data.content;
                  setMessages((prev) =>
                    prev.map((msg) =>
                      msg.id === streamingMessageId
                        ? { ...msg, content: fullContent, streaming: true }
                        : msg
                    )
                  );
                }

                if (data.type === "images" && data.images) {
                  receivedImages = data.images;
                }

                if (data.type === "end") {
                  setMessages((prev) =>
                    prev.map((msg) =>
                      msg.id === streamingMessageId
                        ? { ...msg, streaming: false, status: "complete", relatedImages: receivedImages }
                        : msg
                    )
                  );
                  speakText(fullContent);
                  incrementMessageCount();
                  
                  setOrbState("complete");
                  setTimeout(() => setOrbState("idle"), 2000);
                }

                if (data.type === "error") {
                  throw new Error(data.error || "Streaming error");
                }
              } catch (e) {
                if (e.message === "Streaming error") throw e;
                console.error("Parse error:", e);
              }
            }
          }
        }
      }
    } catch (error) {
      console.error("Error:", error);
      
      setMessages((prev) => prev.filter((msg) => msg.id !== streamingMessageId));
      setMessages((prev) =>
        prev.map((msg) => msg.id === messageId ? { ...msg, status: "failed" } : msg)
      );
      
      setFailedMessage({
        content: currentInput,
        image: currentImage,
        imagePreview: currentImagePreview,
        file: currentFile
      });
      
      setOrbState("error");
      setTimeout(() => setOrbState("idle"), 2000);
    } finally {
      setIsLoading(false);
      setIsSending(false);
      isStreamingRef.current = false;
    }
  };

  const handlePromptSelect = (prompt) => {
    setInput(prompt);
  };

  return (
    <div className="nexi-chat-container" data-testid="chat-interface">
      {/* Messages scroll area */}
      <div 
        ref={messagesContainerRef}
        onScroll={handleScroll}
        className="nexi-messages-scroll"
      >
        <div className="nexi-messages-content">
          <AnimatePresence mode="wait">
            {messages.length === 0 ? (
              <NexiLivingHome 
                key="empty"
                onSelectAction={(action) => {
                  const prompts = {
                    "Chat": "Let's have a conversation",
                    "Research": "Help me research ",
                    "Create": "Help me create ",
                    "Analyze": "Analyze this for me: ",
                    "Images": "Generate an image of ",
                    "Plan": "Help me plan ",
                    "Tools": "What tools can you help me with?",
                    "More": "What else can you do?"
                  };
                  handlePromptSelect(prompts[action] || action);
                }}
                onOrbClick={handleVoiceClick}
                userName={userName !== "Friend" ? userName : null}
                voiceState={voiceState}
                isListening={voiceState === "listening"}
                voiceSupported={voiceSupported}
              />
            ) : (
              <motion.div
                key="messages"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="nexi-message-list"
              >
                {messages.map((message, index) => (
                  <MessageBubbleRedesigned
                    key={message.id || `${index}-${message.role}`}
                    message={message}
                    index={index}
                    conversationId={conversationId}
                    userName={userName}
                  />
                ))}
              </motion.div>
            )}
          </AnimatePresence>

          {/* Loading */}
          {isLoading && messages.filter(m => m.streaming).length === 0 && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="nexi-loading-indicator"
            >
              <NexiOrb state={orbState} size="sm" />
              <span>Nexi is thinking...</span>
            </motion.div>
          )}

          {/* Speaking */}
          {isSpeaking && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="nexi-speaking-indicator"
            >
              <Volume2 className="w-4 h-4 animate-pulse" />
              <span>Speaking...</span>
            </motion.div>
          )}

          {/* Failed message retry */}
          {failedMessage && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="nexi-error-card"
            >
              <AlertCircle className="w-5 h-5 text-red-400" />
              <span>Something went wrong. Try again.</span>
              <button onClick={handleRetry} className="nexi-retry-btn">
                <RotateCcw size={14} />
                Retry
              </button>
            </motion.div>
          )}

          {/* Scroll anchor */}
          <div ref={messagesEndRef} className="nexi-scroll-anchor" />
        </div>
      </div>

      {/* Jump to latest */}
      <AnimatePresence>
        {showScrollButton && messages.length > 0 && (
          <motion.button
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 20 }}
            onClick={() => scrollToBottom(true)}
            className="nexi-jump-btn"
            data-testid="scroll-to-bottom"
          >
            <ChevronDown size={18} />
            <span>New messages</span>
          </motion.button>
        )}
      </AnimatePresence>

      {/* Composer - sticky at bottom */}
      <div className="nexi-composer-area">
        <NexiComposer
          value={input}
          onChange={setInput}
          onSend={handleSend}
          onFileSelect={handleFileSelect}
          onImageSelect={handleImageSelect}
          onCameraSelect={handleImageSelect}
          onVoiceClick={handleVoiceClick}
          isLoading={isLoading || isSending}
          disabled={false}
          selectedFile={selectedFile}
          imagePreview={imagePreview}
          onRemoveFile={handleRemoveFile}
          onRemoveImage={handleRemoveImage}
          voiceState={voiceState}
          voiceSupported={voiceSupported}
          placeholder={
            voiceState === "listening" ? "Listening..." :
            mode === "resume" ? "Describe your experience..." :
            mode === "website" ? "Describe your website idea..." :
            "Ask Nexi anything..."
          }
        />
      </div>
    </div>
  );
};

export default ChatInterfaceRedesigned;
